import { useMemo } from 'react';

import { useGetHomeStatistics } from 'hooks';
import { homePageWidgetPositions } from './constants';
import { useGetHomePageWidgetsQuery } from 'hooks/api/appSettings';
import { DEFAULT_WIDGETS, HomeData, widgetsConfig } from './widgetsConfig';

const EmptySlotWidget: HomeData = {
  title: '',
  tooltip: '',
  icon: null,
  navigateLink: () => '/home',
  items: [],
};

const useGetHomeData = () => {
  const {
    personalizedConfig,
    isCustomized,
    isLoading: isLoadingWidgetsConfig,
  } = useGetHomePageWidgetsQuery();

  const configuredWidgets = useMemo<string[]>(() => {
    if (isLoadingWidgetsConfig) return [];
    if (!isCustomized) return DEFAULT_WIDGETS;

    return homePageWidgetPositions
      .map((position) => personalizedConfig?.[position] ?? '')
      .filter(Boolean);
  }, [isCustomized, isLoadingWidgetsConfig, personalizedConfig]);

  const { data, isPending } = useGetHomeStatistics(
    configuredWidgets,
    !isLoadingWidgetsConfig,
  );

  const newData = useMemo(() => {
    if (isPending || isLoadingWidgetsConfig) return new Array(4).fill({});

    if (!isCustomized) {
      return DEFAULT_WIDGETS.map((widgetKey) => {
        const cfg = widgetsConfig[widgetKey];

        if (!cfg) return EmptySlotWidget;

        return {
          ...cfg,
          items: cfg.query.sortAndMap(data[widgetKey] ?? []),
        };
      });
    }

    return homePageWidgetPositions.map((position) => {
      const widgetKey = personalizedConfig?.[position] ?? '';
      const cfg = widgetsConfig[widgetKey];

      if (!widgetKey || !cfg) {
        if (!cfg) {
          console.warn(
            `Unsupported widget key "${widgetKey}" for position "${position}"`,
          );
        }

        return EmptySlotWidget;
      }

      return {
        ...cfg,
        items: cfg.query.sortAndMap(data[widgetKey] ?? []),
      };
    });
  }, [
    data,
    isCustomized,
    isLoadingWidgetsConfig,
    isPending,
    personalizedConfig,
  ]);

  return {
    data: newData,
    isPending: isPending || isLoadingWidgetsConfig,
  };
};

export type { HomeData };
export { useGetHomeData };
